/**
 * `/pruefliste` — was der Sweeper in den **persönlichen** Postfächern fand.
 *
 * Der Sweeper durchsucht die Postfächer von Henri und Anton nach Anhängen, die
 * aussehen wie Belege. Er darf sich nicht irren dürfen: ein Beleg von hier wird
 * **nicht** ausgelesen und geht **nicht** nach DATEV, bevor ein Mensch den
 * Haken gesetzt hat (§15.2). Deshalb ist das hier eine Liste mit zwei Knöpfen
 * und keine Automatik.
 *
 * **Wer darf hier lesen?** Der Inhaber des Postfachs und `admin` — sonst
 * niemand. Das ist keine Rechteverwaltung um ihrer selbst willen: in diesen
 * Postfächern liegt Privatpost. Der Server filtert bereits nach Postfach; die
 * Seite filtert noch einmal (`lib/rechte.ts`, dort geprüft). Zwei Schlösser an
 * einer Tür sind hier angemessen.
 *
 * **Das Kreuz verlangt einen Grund.** Die Datei geht danach weg — das Original
 * liegt in Gmail —, und übrig bleibt eine Log-Zeile mit `sha256`,
 * `message_id` und Grund. Ohne Pflichtfeld wäre daraus später nicht mehr
 * abzulesen, warum etwas nie ein Beleg wurde.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Eye, Mail, Paperclip, X } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import { datumZeit } from '@/lib/format';
import { ENTSCHEIDUNG_LABEL, VERWERF_GRUND_LABEL } from '@/lib/beleg';
import { darfEntscheiden, istAdmin, sichtbareEintraege } from '@/lib/rechte';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Dialog } from '@/components/ui/Dialog';
import { Auswahl, Feld, Seitenkopf, Textfeld } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band, Fehler, Laden, Leer } from '@/components/ui/Zustaende';
import type { ReviewItemResponse, VerwerfGrund } from '@/types';

const GRUENDE = Object.keys(VERWERF_GRUND_LABEL) as VerwerfGrund[];

export function PrueflistePage() {
  const { user } = useAuth();
  const cache = useQueryClient();
  const toast = useToast();

  const [nurOffen, setNurOffen] = useState(true);
  const [verwerfen, setVerwerfen] = useState<ReviewItemResponse | null>(null);
  const [vorschau, setVorschau] = useState<ReviewItemResponse | null>(null);

  const liste = useQuery({
    queryKey: ['pruefliste', nurOffen],
    queryFn: () => bh.pruefliste(nurOffen),
  });

  const entscheiden = useMutation({
    mutationFn: (arg: { id: string; decision: 'uebernommen' | 'verworfen'; reason?: VerwerfGrund }) =>
      bh.prueflisteEntscheiden(arg.id, { decision: arg.decision, reason: arg.reason ?? null }),
    onSuccess: (antwort) => {
      void cache.invalidateQueries({ queryKey: ['pruefliste'] });
      void cache.invalidateQueries({ queryKey: ['belege'] });
      setVerwerfen(null);
      toast.erfolg(
        antwort.decision === 'uebernommen'
          ? 'Übernommen. Der Beleg liegt jetzt im Eingang.'
          : 'Verworfen. Der Grund steht im Log.',
      );
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  if (!user) return <Laden />;

  if (liste.isLoading) return <Laden text="Prüfliste wird geladen …" />;
  if (liste.isError || !liste.data) {
    return (
      <div>
        <Seitenkopf titel="Prüfliste" />
        <Fehler text={fehlerText(liste.error)} onErneut={() => void liste.refetch()} />
      </div>
    );
  }

  // Der Server filtert schon. Hier wird es wiederholt, weil es fremde Post
  // ist — und zwar stumm: „3 Einträge werden dir nicht gezeigt" wäre selbst
  // eine Auskunft über fremde Post, nämlich wie viel davon es gerade gibt.
  const eintraege = sichtbareEintraege(user, liste.data.items);

  return (
    <div className="max-w-4xl">
      <Seitenkopf
        titel="Prüfliste"
        beschreibung="Was der Sweeper in den persönlichen Postfächern gefunden hat. Erst mit dem Haken wird daraus ein Beleg."
      >
        <Button variant="outline" size="sm" onClick={() => setNurOffen((b) => !b)}>
          {nurOffen ? 'Auch Entschiedenes zeigen' : 'Nur Offene zeigen'}
        </Button>
      </Seitenkopf>

      <div className="mb-5 space-y-2">
        <Band>
          {istAdmin(user) ? (
            <>
              Als Admin siehst du alle Postfächer. Die Funde stammen aus privater Post — was hier
              nicht hingehört, bekommt ein Kreuz mit Grund und ist damit weg.
            </>
          ) : (
            <>
              Du siehst nur die Funde aus deinem eigenen Postfach ({user.email}). Fremde Postfächer
              bleiben fremd.
            </>
          )}
        </Band>
      </div>

      {eintraege.length === 0 ? (
        <Leer
          titel={nurOffen ? 'Nichts zu prüfen.' : 'Keine Funde.'}
          hinweis={
            nurOffen
              ? 'Der Sweeper hat nichts Neues gefunden — oder es ist schon entschieden. Wann er zuletzt lief, steht am Punkt oben rechts.'
              : 'In diesem Postfach hat der Sweeper noch nie etwas gefunden.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {eintraege.map((eintrag) => (
            <Zeile
              key={eintrag.id}
              eintrag={eintrag}
              darfEntscheidenHier={darfEntscheiden(user, eintrag)}
              laeuft={entscheiden.isPending}
              onUebernehmen={() =>
                entscheiden.mutate({ id: eintrag.id, decision: 'uebernommen' })
              }
              onVerwerfen={() => setVerwerfen(eintrag)}
              onVorschau={() => setVorschau(eintrag)}
            />
          ))}
        </ul>
      )}

      <VorschauDialog eintrag={vorschau} onSchliessen={() => setVorschau(null)} />

      <VerwerfenDialog
        eintrag={verwerfen}
        laeuft={entscheiden.isPending}
        onAbbrechen={() => setVerwerfen(null)}
        onVerwerfen={(reason) =>
          verwerfen && entscheiden.mutate({ id: verwerfen.id, decision: 'verworfen', reason })
        }
      />
    </div>
  );
}

/**
 * Eine Zeile — Postfach, Absender, Betreff, Anhang, Datum.
 *
 * Der Betreff steht gross, weil man daran erkennt, worum es geht. Postfach und
 * Absender stehen klein darunter: Sie beantworten die Frage „darf ich das
 * überhaupt sehen" und „kommt das von aussen", nicht die Frage „was ist das".
 */
function Zeile({
  eintrag,
  darfEntscheidenHier,
  laeuft,
  onUebernehmen,
  onVerwerfen,
  onVorschau,
}: {
  eintrag: ReviewItemResponse;
  darfEntscheidenHier: boolean;
  laeuft: boolean;
  onUebernehmen: () => void;
  onVerwerfen: () => void;
  onVorschau: () => void;
}) {
  const offen = eintrag.decision === 'offen';

  return (
    <li className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">
            {eintrag.subject ?? '(ohne Betreff)'}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Mail size={12} />
              {eintrag.mailbox}
            </span>
            <span>von {eintrag.sender_address ?? 'unbekannt'}</span>
            <span>{datumZeit(eintrag.created_at)}</span>
          </p>
          <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
            {eintrag.original_name && (
              <Chip titel={eintrag.mime_type ?? undefined}>
                <Paperclip size={11} />
                {eintrag.original_name}
              </Chip>
            )}
            {eintrag.amount_hinweis && (
              <Chip titel="Was die Heuristik im Betreff gefunden hat — ohne Modellaufruf, deshalb nur ein Hinweis.">
                Hinweis: {eintrag.amount_hinweis}
              </Chip>
            )}
            {!offen && (
              <Chip>
                {ENTSCHEIDUNG_LABEL[eintrag.decision]}
                {eintrag.reason ? ` · ${VERWERF_GRUND_LABEL[eintrag.reason]}` : ''}
                {eintrag.decided_at ? ` · ${datumZeit(eintrag.decided_at)}` : ''}
              </Chip>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onVorschau} title="Was über diesen Fund bekannt ist">
            <Eye size={14} className="mr-1.5" />
            Vorschau
          </Button>
          {eintrag.document_id && (
            <Link
              to={`/eingang/${eintrag.document_id}`}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              <ExternalLink size={12} />
              Zum Beleg
            </Link>
          )}
          {offen && darfEntscheidenHier && (
            <>
              <Button
                size="sm"
                onClick={onUebernehmen}
                disabled={laeuft}
                title="Daraus wird ein Beleg im Eingang. Erst dann wird ausgelesen."
              >
                <Check size={14} className="mr-1.5" />
                Übernehmen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onVerwerfen}
                disabled={laeuft}
                title="Kein Beleg. Der Grund ist Pflicht — die Datei geht danach weg."
              >
                <X size={14} className="mr-1.5" />
                Verwerfen
              </Button>
            </>
          )}
          {offen && !darfEntscheidenHier && (
            <span className="text-xs text-slate-400">nur der Postfach-Inhaber entscheidet</span>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Vorschau — was über diesen Fund bekannt ist.
 *
 * **Die Datei selbst ist hier noch nicht zu sehen.** Das ist kein Versäumnis
 * der Oberfläche: `review_items` hat keine Route, die den Anhang ausliefert,
 * und das aus gutem Grund — vor dem Haken existiert kein Beleg, also auch
 * kein `GET /documents/{id}/file`. Ob es eine geben soll (und wer sie sehen
 * dürfte, wenn im selben Postfach Privatpost liegt), ist eine Entscheidung
 * und keine Programmieraufgabe; sie steht im Morgenprotokoll.
 *
 * Bis dahin steht hier, was der Sweeper mitgeschickt hat. Das reicht in den
 * meisten Fällen: Betreff und Absender sagen, ob es eine Rechnung ist.
 */
function VorschauDialog({
  eintrag,
  onSchliessen,
}: {
  eintrag: ReviewItemResponse | null;
  onSchliessen: () => void;
}) {
  return (
    <Dialog
      offen={eintrag !== null}
      onSchliessen={onSchliessen}
      titel="Fund ansehen"
      hinweis="Was der Sweeper zu diesem Anhang mitgeschickt hat."
      fussleiste={
        <Button variant="outline" onClick={onSchliessen}>
          Schließen
        </Button>
      }
    >
      {eintrag && (
        <dl className="space-y-1.5 text-sm">
          <Angabe name="Betreff" wert={eintrag.subject ?? '(ohne Betreff)'} />
          <Angabe name="Postfach" wert={eintrag.mailbox} />
          <Angabe name="Absender" wert={eintrag.sender_address ?? 'unbekannt'} />
          <Angabe name="Anhang" wert={eintrag.original_name ?? '—'} />
          <Angabe name="Dateityp" wert={eintrag.mime_type ?? '—'} />
          <Angabe name="Hinweis aus dem Betreff" wert={eintrag.amount_hinweis ?? '—'} />
          <Angabe name="Eingegangen" wert={datumZeit(eintrag.created_at)} />
          <Angabe name="Message-ID" wert={eintrag.message_id} />
        </dl>
      )}

      <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
        Die Datei selbst lässt sich hier noch nicht öffnen — vor dem Haken gibt es keinen Beleg
        und damit keine Adresse für den Anhang. Die Route dafür folgt.
      </p>
    </Dialog>
  );
}

function Angabe({ name, wert }: { name: string; wert: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-44 shrink-0 text-slate-500">{name}</dt>
      <dd className="min-w-0 flex-1 break-words text-slate-800">{wert}</dd>
    </div>
  );
}

/** Das Kreuz — nie `window.confirm`, weil der Grund Pflicht ist. */
function VerwerfenDialog({
  eintrag,
  laeuft,
  onAbbrechen,
  onVerwerfen,
}: {
  eintrag: ReviewItemResponse | null;
  laeuft: boolean;
  onAbbrechen: () => void;
  onVerwerfen: (grund: VerwerfGrund) => void;
}) {
  const [grund, setGrund] = useState<VerwerfGrund>('newsletter');
  const [notiz, setNotiz] = useState('');

  return (
    <Dialog
      offen={eintrag !== null}
      onSchliessen={onAbbrechen}
      titel="Kein Beleg"
      hinweis="Die Datei geht weg — das Original bleibt in Gmail. Übrig bleibt eine Log-Zeile mit Prüfsumme, Message-ID und Grund."
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant="danger" onClick={() => onVerwerfen(grund)} loading={laeuft}>
            Verwerfen
          </Button>
        </>
      }
    >
      {eintrag && (
        <p className="mb-4 text-sm text-slate-600">
          <span className="font-medium text-slate-900">
            {eintrag.subject ?? '(ohne Betreff)'}
          </span>
          <span className="block text-xs text-slate-500">
            {eintrag.mailbox} · {eintrag.original_name ?? 'ohne Anhang'}
          </span>
        </p>
      )}

      <div className="space-y-3">
        <Feld label="Grund" pflicht hinweis="Steht im Log und bleibt dort.">
          <Auswahl value={grund} onChange={(e) => setGrund(e.target.value as VerwerfGrund)}>
            {GRUENDE.map((g) => (
              <option key={g} value={g}>
                {VERWERF_GRUND_LABEL[g]}
              </option>
            ))}
          </Auswahl>
        </Feld>

        {/* Der Freitext geht (noch) nicht mit: `ReviewEntscheidung` kennt nur
            `decision` und `reason`. Er steht trotzdem hier, weil man beim
            Verwerfen etwas sagen will — und was der Vertrag nicht hergibt,
            gehört ins Morgenprotokoll und nicht in eine stille Auslassung. */}
        <Feld
          label="Anmerkung (freiwillig)"
          hinweis="Wird derzeit noch nicht mitgeschickt — das Feld im Vertrag folgt."
        >
          <Textfeld
            rows={2}
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="Werbepost der Kammer"
          />
        </Feld>
      </div>
    </Dialog>
  );
}
